import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LockKey, EnvelopeSimple, ShieldCheck, ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

function OtpInput({ value, onChange, autoFocus }) {
    const inputs = useRef([]);
    const digits = (value + "").split("").concat(Array(6).fill("")).slice(0, 6);

    const setAt = (i, ch) => {
        const arr = digits.slice();
        arr[i] = ch;
        onChange(arr.join(""));
    };

    return (
        <div className="flex justify-center gap-2">
            {digits.map((d, i) => (
                <input
                    key={i}
                    ref={(el) => (inputs.current[i] = el)}
                    data-testid={`totp-digit-${i}`}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus={autoFocus && i === 0}
                    maxLength={1}
                    value={d}
                    onChange={(e) => {
                        const ch = e.target.value.replace(/\D/g, "").slice(-1);
                        setAt(i, ch);
                        if (ch && i < 5) inputs.current[i + 1]?.focus();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Backspace" && !d && i > 0) inputs.current[i - 1]?.focus();
                    }}
                    onPaste={(e) => {
                        const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
                        if (paste.length >= 1) {
                            e.preventDefault();
                            onChange(paste.padEnd(6, "").slice(0, 6));
                            inputs.current[Math.min(paste.length, 5)]?.focus();
                        }
                    }}
                    className="glass-input !p-0 h-14 w-11 text-center text-2xl font-semibold mono"
                />
            ))}
        </div>
    );
}

export default function LoginPage() {
    const { user, login, verifyTotp, setupTotpConfirm } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState("credentials"); // credentials | totp_verify | totp_setup
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [pendingToken, setPendingToken] = useState(null);
    const [totpUri, setTotpUri] = useState(null);
    const [totpSecret, setTotpSecret] = useState(null);
    const [qrDataUrl, setQrDataUrl] = useState(null);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (user) navigate("/dashboard", { replace: true });
    }, [user, navigate]);

    const submitCredentials = async (e) => {
        e.preventDefault();
        setErr(null);
        setBusy(true);
        try {
            const data = await login(email.trim().toLowerCase(), password);
            setPendingToken(data.pending_token);
            if (data.step === "totp_setup") {
                setTotpUri(data.totp_uri);
                setTotpSecret(data.totp_secret);
                setQrDataUrl(data.qr_data_url);
                setStep("totp_setup");
            } else {
                setStep("totp_verify");
            }
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    };

    const submitCode = async (e) => {
        e.preventDefault();
        if (code.length < 6) return;
        setErr(null);
        setBusy(true);
        try {
            if (step === "totp_setup") await setupTotpConfirm(pendingToken, code);
            else await verifyTotp(pendingToken, code);
            navigate("/dashboard", { replace: true });
        } catch (e2) {
            setErr(formatApiError(e2));
            setCode("");
        } finally {
            setBusy(false);
        }
    };

    const goBack = () => {
        setStep("credentials");
        setCode("");
        setErr(null);
        setPendingToken(null);
        setTotpUri(null);
        setTotpSecret(null);
        setQrDataUrl(null);
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="glass-strong w-full max-w-md p-10"
            >
                <div className="flex flex-col items-center mb-6">
                    <div className="logo-sphere mb-4" style={{ width: 72, height: 72 }} />
                    <h1
                        className="text-3xl font-extrabold"
                        style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                    >
                        DomainMigInt
                    </h1>
                    <p className="text-sm text-[color:var(--dm-text-2)] mt-1">
                        Admin console
                    </p>
                </div>

                {step === "credentials" && (
                    <form onSubmit={submitCredentials} className="space-y-4">
                        <label className="block">
                            <span className="text-xs font-semibold text-[color:var(--dm-text-2)] flex items-center gap-1.5 mb-1.5">
                                <EnvelopeSimple size={14} weight="duotone" /> Email
                            </span>
                            <input
                                data-testid="login-email-input"
                                type="email"
                                required
                                autoFocus
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="glass-input"
                                placeholder="you@company.com"
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs font-semibold text-[color:var(--dm-text-2)] flex items-center gap-1.5 mb-1.5">
                                <LockKey size={14} weight="duotone" /> Password
                            </span>
                            <input
                                data-testid="login-password-input"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="glass-input"
                            />
                        </label>
                        {err && (
                            <div className="pill pill-danger w-full !justify-start !py-2 !px-3 text-[0.75rem]">
                                {err}
                            </div>
                        )}
                        <button
                            data-testid="login-submit-button"
                            type="submit"
                            disabled={busy}
                            className="btn-glossy w-full"
                        >
                            {busy ? "Signing in…" : (
                                <>
                                    Continue <ArrowRight size={16} weight="bold" />
                                </>
                            )}
                        </button>
                    </form>
                )}

                {step === "totp_setup" && (
                    <div className="space-y-5">
                        <div className="text-center">
                            <ShieldCheck size={28} weight="duotone" className="mx-auto text-[color:var(--dm-brand)]" />
                            <h2 className="mt-2 text-lg font-bold">Set up 2FA</h2>
                            <p className="text-xs text-[color:var(--dm-text-2)] mt-1">
                                Scan this code with Google Authenticator, then enter the 6-digit code
                            </p>
                        </div>
                        {qrDataUrl && (
                            <div className="flex justify-center">
                                <img
                                    src={qrDataUrl}
                                    alt="TOTP QR"
                                    className="w-48 h-48 rounded-2xl p-2 bg-white shadow-[0_8px_28px_rgba(0,122,255,0.15)]"
                                />
                            </div>
                        )}
                        {totpSecret && (
                            <div className="text-center">
                                <div className="text-[0.65rem] uppercase tracking-wider text-[color:var(--dm-text-2)] mb-1">
                                    Or type this secret
                                </div>
                                <code
                                    data-testid="totp-setup-secret"
                                    className="mono text-xs bg-[rgba(0,122,255,0.08)] px-2 py-1 rounded-md"
                                >
                                    {totpSecret}
                                </code>
                            </div>
                        )}
                        <form onSubmit={submitCode} className="space-y-3">
                            <OtpInput value={code} onChange={setCode} autoFocus />
                            {err && (
                                <div className="pill pill-danger w-full !justify-start !py-2 !px-3 text-[0.75rem]">
                                    {err}
                                </div>
                            )}
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={goBack}
                                    className="btn-ghost"
                                    data-testid="totp-back-button"
                                >
                                    <ArrowLeft size={14} weight="bold" /> Back
                                </button>
                                <button
                                    data-testid="totp-setup-confirm-button"
                                    type="submit"
                                    disabled={busy || code.length < 6}
                                    className="btn-glossy flex-1"
                                >
                                    {busy ? "Verifying…" : "Enable & sign in"}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {step === "totp_verify" && (
                    <div className="space-y-5">
                        <div className="text-center">
                            <ShieldCheck size={28} weight="duotone" className="mx-auto text-[color:var(--dm-brand)]" />
                            <h2 className="mt-2 text-lg font-bold">Two-factor authentication</h2>
                            <p className="text-xs text-[color:var(--dm-text-2)] mt-1">
                                Enter the 6-digit code from your authenticator app
                            </p>
                        </div>
                        <form onSubmit={submitCode} className="space-y-3">
                            <OtpInput value={code} onChange={setCode} autoFocus />
                            {err && (
                                <div className="pill pill-danger w-full !justify-start !py-2 !px-3 text-[0.75rem]">
                                    {err}
                                </div>
                            )}
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={goBack}
                                    className="btn-ghost"
                                    data-testid="totp-back-button"
                                >
                                    <ArrowLeft size={14} weight="bold" /> Back
                                </button>
                                <button
                                    data-testid="totp-verify-button"
                                    type="submit"
                                    disabled={busy || code.length < 6}
                                    className="btn-glossy flex-1"
                                >
                                    {busy ? "Verifying…" : "Sign in"}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </motion.div>
        </div>
    );
}
